import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, UserCheck, ServerCog, LineChart, Briefcase, Handshake, Scale } from "lucide-react";
import { useLang, useAuth } from "@/context/AppContext";
import { Logo, GlobeMark } from "@/components/Logo";
import { LangSwitch } from "@/components/LangSwitch";
import { Waitlist } from "@/components/landing/Waitlist";

const HERO_IMG = "https://images.unsplash.com/photo-1760800324450-21043d3d8ee4?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";
const CASE_IMGS = [
  "https://images.unsplash.com/photo-1758599543278-32d9d073941e?crop=entropy&cs=srgb&fm=jpg&q=80&w=900",
  "https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?crop=entropy&cs=srgb&fm=jpg&q=80&w=900",
  "https://images.unsplash.com/photo-1684503830683-108f3e0fd03f?crop=entropy&cs=srgb&fm=jpg&q=80&w=900",
];

const Kicker = ({ children }) => <p className="font-mono text-xs uppercase tracking-[0.25em] text-emerald-400/90">{children}</p>;

const Nav = () => {
  const { t } = useLang();
  const { user } = useAuth();
  return (
    <header className="fixed inset-x-0 top-0 z-40 glass border-b border-white/5">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-12">
        <Link to="/" data-testid="nav-home-link"><Logo /></Link>
        <nav className="hidden items-center gap-8 text-sm text-slate-300 md:flex">
          {[["vision", "nav_vision"], ["how", "nav_how"], ["cases", "nav_cases"], ["waitlist", "nav_waitlist"]].map(([id, k]) => (
            <a key={id} href={`#${id}`} data-testid={`nav-${id}-link`} className="transition-colors duration-200 hover:text-emerald-300">{t(k)}</a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <LangSwitch />
          <Link to={user ? "/app" : "/login"} data-testid="nav-login-btn" className="rounded-full border border-emerald-400/40 px-4 py-2 text-sm text-emerald-300 transition-colors duration-200 hover:bg-emerald-400 hover:text-slate-950">
            {user ? t("nav_app") : t("nav_login")}
          </Link>
        </div>
      </div>
    </header>
  );
};

const Hero = () => {
  const { t } = useLang();
  return (
    <section id="vision" className="relative overflow-hidden grain pt-36 pb-24 lg:pt-44 lg:pb-32">
      <img src={HERO_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F17]/40 via-[#0B0F17]/80 to-[#0B0F17]" />
      <div className="absolute inset-0 grid-lines" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-4 sm:px-6 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-7">
          <div className="rise"><Kicker>{t("hero_kicker")}</Kicker></div>
          <h1 className="rise mt-6 font-display text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl" style={{ animationDelay: "80ms" }} data-testid="hero-title">
            {t("hero_title_1")} <span className="text-emerald-400">{t("hero_title_2")}</span>
          </h1>
          <p className="rise mt-8 max-w-xl text-base leading-relaxed text-slate-300" style={{ animationDelay: "160ms" }}>{t("hero_sub")}</p>
          <div className="rise mt-10 flex flex-wrap gap-4" style={{ animationDelay: "240ms" }}>
            <a href="#waitlist" data-testid="hero-waitlist-cta" className="group inline-flex items-center gap-2 rounded-full bg-emerald-500 px-6 py-3 text-sm font-semibold text-slate-950 transition-transform duration-200 hover:-translate-y-0.5">
              {t("hero_cta")} <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </a>
            <a href="#how" data-testid="hero-how-cta" className="rounded-full border border-white/15 px-6 py-3 text-sm text-slate-200 transition-colors duration-200 hover:border-white/40">{t("hero_cta2")}</a>
          </div>
          <p className="rise mt-12 inline-flex items-center gap-3 border-l-2 border-yellow-400 pl-4 font-mono text-xs uppercase tracking-widest text-yellow-300" style={{ animationDelay: "320ms" }} data-testid="hero-rule">
            {t("rule")}
          </p>
        </div>
        <div className="relative hidden lg:col-span-5 lg:block">
          <div className="relative mx-auto aspect-square w-full max-w-md">
            <div className="orbit absolute inset-0 rounded-full border border-emerald-400/20" />
            <div className="orbit-rev absolute inset-10 rounded-full border border-dashed border-cyan-400/20" />
            <div className="absolute inset-20 grid place-items-center rounded-full border border-white/10 bg-[#111827]/60 text-slate-200">
              <GlobeMark className="h-40 w-40" />
            </div>
            {[[8, 30], [85, 18], [92, 70], [15, 82], [50, 2]].map(([x, y], i) => (
              <span key={`${x}-${y}`} className="pulse-node absolute h-2.5 w-2.5 rounded-full bg-emerald-400" style={{ left: `${x}%`, top: `${y}%`, animationDelay: `${i * 0.5}s` }} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const Sequence = () => {
  const { t } = useLang();
  const steps = ["intent", "trust", "connection", "action", "result"];
  const tones = ["text-amber-300", "text-cyan-300", "text-emerald-300", "text-yellow-300", "text-slate-200"];
  return (
    <section className="border-y border-white/5 bg-[#0d121c]">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-px bg-white/5 md:grid-cols-5">
        {steps.map((s, i) => (
          <div key={s} className="group bg-[#0d121c] p-8 transition-colors duration-300 hover:bg-[#111827]" data-testid={`sequence-step-${s}`}>
            <span className="font-mono text-xs text-slate-500">0{i + 1}</span>
            <h3 className={`mt-4 font-display text-xl font-bold ${tones[i]}`}>{t(`seq_${s}`)}</h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">{t(`seq_${s}_d`)}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

const Benefits = () => {
  const { t } = useLang();
  const items = [[ShieldCheck, 1], [UserCheck, 2], [ServerCog, 3], [LineChart, 4]];
  return (
    <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-12 lg:py-32">
      <Kicker>{t("ben_kicker")}</Kicker>
      <h2 className="mt-4 max-w-2xl font-display text-3xl font-bold tracking-tight text-white lg:text-4xl">{t("ben_title")}</h2>
      <div className="mt-16 grid gap-6 md:grid-cols-2">
        {items.map(([Icon, n], i) => (
          <div key={n} className={`rounded-md border border-white/[0.08] bg-[#111827] p-8 transition-transform duration-300 hover:-translate-y-1 ${i % 2 ? "md:translate-y-10" : ""}`} data-testid={`benefit-${n}`}>
            <Icon className="h-6 w-6 text-emerald-400" strokeWidth={1.5} />
            <h3 className="mt-6 font-display text-xl font-bold text-white">{t(`ben_${n}`)}</h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">{t(`ben_${n}d`)}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

const How = () => {
  const { t } = useLang();
  return (
    <section id="how" className="border-t border-white/5 bg-[#0d121c] py-24 lg:py-32">
      <div className="mx-auto grid max-w-7xl gap-16 px-4 sm:px-6 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-4">
          <Kicker>{t("how_kicker")}</Kicker>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-white lg:text-4xl">{t("how_title")}</h2>
          <p className="mt-8 border-l-2 border-yellow-400 pl-4 font-mono text-xs uppercase tracking-widest text-yellow-300">{t("rule")}</p>
        </div>
        <ol className="space-y-px lg:col-span-8">
          {[1, 2, 3, 4, 5].map((n) => (
            <li key={n} className="flex gap-8 border-b border-white/5 py-8" data-testid={`how-step-${n}`}>
              <span className="font-display text-4xl font-bold text-emerald-400/40">{n}</span>
              <div>
                <h3 className="font-display text-xl font-bold text-white">{t(`how_${n}`)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{t(`how_${n}d`)}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};

const Cases = () => {
  const { t } = useLang();
  const icons = [Briefcase, Handshake, Scale];
  return (
    <section id="cases" className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-12 lg:py-32">
      <Kicker>{t("cases_kicker")}</Kicker>
      <h2 className="mt-4 max-w-3xl font-display text-3xl font-bold tracking-tight text-white lg:text-4xl">{t("cases_title")}</h2>
      <div className="mt-16 grid gap-6 md:grid-cols-3">
        {[1, 2, 3].map((n, i) => {
          const Icon = icons[i];
          return (
            <article key={n} className="group overflow-hidden rounded-md border border-white/[0.08] bg-[#111827]" data-testid={`use-case-${n}`}>
              <div className="h-48 overflow-hidden">
                <img src={CASE_IMGS[i]} alt="" className="h-full w-full object-cover opacity-70 transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="p-8">
                <Icon className="h-5 w-5 text-cyan-300" strokeWidth={1.5} />
                <h3 className="mt-4 font-display text-xl font-bold text-white">{t(`case_${n}`)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{t(`case_${n}d`)}</p>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default function Landing() {
  const { t } = useLang();
  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100">
      <Nav />
      <Hero />
      <Sequence />
      <Benefits />
      <How />
      <Cases />
      <Waitlist />
      <footer className="border-t border-white/5 py-12">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 sm:px-6 md:flex-row md:items-center md:justify-between lg:px-12">
          <Logo />
          <p className="font-mono text-xs text-slate-500" data-testid="footer-status">{t("footer_status")}</p>
          <p className="font-mono text-xs text-slate-500">© {new Date().getFullYear()} AYDORIQ Technology</p>
        </div>
      </footer>
    </div>
  );
}
